const CONFIRM_PASSWORD_RESET_MUTATION = `
  mutation ConfirmPasswordReset($confirmPasswordReset: ConfirmPasswordResetInput!) {
    confirmPasswordReset(confirmPasswordReset: $confirmPasswordReset) {
      result
    }
  }
`;

export type ConfirmPasswordResetErrorCode =
  | 'PASSWORD_RESET_USER_NOT_FOUND'
  | 'PASSWORD_RESET_TOKEN_EXPIRED'
  | 'PASSWORD_RESET_TOKEN_INVALID'
  | 'PASSWORD_RESET_CURRENT_PASSWORD'
  | 'PASSWORD_INPUT_INVALID'
  | 'UNKNOWN_ERROR';

type GraphQLErrorResponse = {
  message?: string;
  extensions?: {
    code?: string;
  };
};

type ConfirmPasswordResetGraphQLResponse = {
  data?: {
    confirmPasswordReset?: {
      result?: string | null;
    } | null;
  } | null;
  errors?: GraphQLErrorResponse[];
};

export class ConfirmPasswordResetError extends Error {
  code: ConfirmPasswordResetErrorCode;

  constructor(code: ConfirmPasswordResetErrorCode, message?: string) {
    super(message || code);
    this.name = 'ConfirmPasswordResetError';
    this.code = code;
  }
}

const KNOWN_ERROR_CODES: readonly ConfirmPasswordResetErrorCode[] = [
  'PASSWORD_RESET_USER_NOT_FOUND',
  'PASSWORD_RESET_TOKEN_EXPIRED',
  'PASSWORD_RESET_TOKEN_INVALID',
  'PASSWORD_RESET_CURRENT_PASSWORD',
  'PASSWORD_INPUT_INVALID',
  'UNKNOWN_ERROR',
];

const isKnownErrorCode = (
  code: unknown
): code is ConfirmPasswordResetErrorCode =>
  typeof code === 'string' &&
  KNOWN_ERROR_CODES.includes(code as ConfirmPasswordResetErrorCode);

const getFirstGraphQLError = (
  body?: ConfirmPasswordResetGraphQLResponse
): ConfirmPasswordResetError | undefined => {
  const error = body?.errors?.[0];

  if (!error) {
    return undefined;
  }

  const code = isKnownErrorCode(error.extensions?.code)
    ? error.extensions.code
    : 'UNKNOWN_ERROR';

  return new ConfirmPasswordResetError(code, error.message);
};

const readJson = async (
  response: Response
): Promise<ConfirmPasswordResetGraphQLResponse | undefined> => {
  try {
    return (await response.json()) as ConfirmPasswordResetGraphQLResponse;
  } catch {
    return undefined;
  }
};

interface ConfirmPasswordResetOptions {
  baseUrl: string;
  confirmationToken: string;
  password: string;
}

export const confirmPasswordReset = async ({
  baseUrl,
  confirmationToken,
  password,
}: ConfirmPasswordResetOptions): Promise<void> => {
  const response = await fetch(new URL('/api/graphql', baseUrl).toString(), {
    method: 'POST',
    credentials: 'omit',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      query: CONFIRM_PASSWORD_RESET_MUTATION,
      variables: {
        confirmPasswordReset: {
          confirmationToken,
          password,
        },
      },
    }),
  });
  const body = await readJson(response);
  const graphQLError = getFirstGraphQLError(body);

  if (graphQLError) {
    throw graphQLError;
  }

  if (!response.ok || !body?.data?.confirmPasswordReset?.result) {
    throw new ConfirmPasswordResetError('UNKNOWN_ERROR');
  }
};
